
import React, { useState, useEffect, useRef } from "react";
import { Calendar, CalendarDays, Clock, User, BarChart3, Settings, PanelLeft, TrendingUp, MessageSquare, CheckCircle2, LogOut, Gift, ShieldCheck } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import SylvaLogo from "/sylva-platform-logo.svg";
import { useQueryClient } from '@tanstack/react-query';
import { useTenant } from '@/hooks/useTenant';
import { fetchServices } from '@/lib/queries/services';
import { useAuth } from '@/hooks/useAuth';
import { usePreserveSearchParams } from '@/hooks/usePreserveSearchParams';
import { useUnreadConversationsCount } from '@/hooks/useUnreadConversationsCount';
import { useTasks } from '@/hooks/useTasks';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { useIsStaff } from '@/hooks/useIsStaff';
import { filterNavItemsForStaff } from '@/lib/staffAccess';
import { staffHasFullCustomerAccess } from '@/lib/staffLoginAdmin';
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useIsPlatformAdmin } from "@/hooks/useAdminConsole";

type SidebarMode = "expanded" | "collapsed" | "hover";

interface SidebarProps {
  onNavigate?: () => void;
  collapsed?: boolean;
  sidebarMode?: SidebarMode;
  onSidebarModeChange?: (mode: SidebarMode) => void;
  onHoverChange?: (hovered: boolean) => void;
  activeView?: string;
  onViewChange?: (view: string) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ onNavigate, collapsed = false, sidebarMode = "expanded", onSidebarModeChange, onHoverChange }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { currentTenant, tenants, setCurrentTenant } = useTenant();
  const { signOut } = useAuth();
  const { createPath } = usePreserveSearchParams();
  const { toast } = useToast();

  // Hover-to-expand stability: keep the bar expanded while a menu inside it is
  // open (its dropdown portals outside the bar, which would otherwise fire
  // mouseleave and collapse it mid-interaction), and debounce collapse to avoid
  // edge/animation flicker.
  const [isHovered, setIsHovered] = useState(false);
  const [tenantMenuOpen, setTenantMenuOpen] = useState(false);
  const [controlMenuOpen, setControlMenuOpen] = useState(false);
  const collapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Report a single "expand" intent to the layout. Expanding is instant;
  // collapsing is debounced so the bar doesn't flicker on edge moves or when a
  // menu (tenant switcher / sidebar control) closes.
  useEffect(() => {
    const wantExpanded = isHovered || tenantMenuOpen || controlMenuOpen;
    if (wantExpanded) {
      if (collapseTimerRef.current) {
        clearTimeout(collapseTimerRef.current);
        collapseTimerRef.current = null;
      }
      onHoverChange?.(true);
      return;
    }
    collapseTimerRef.current = setTimeout(() => onHoverChange?.(false), 200);
    return () => {
      if (collapseTimerRef.current) {
        clearTimeout(collapseTimerRef.current);
        collapseTimerRef.current = null;
      }
    };
  }, [isHovered, tenantMenuOpen, controlMenuOpen, onHoverChange]);

  const handleSidebarMouseEnter = () => setIsHovered(true);
  const handleSidebarMouseLeave = () => setIsHovered(false);

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (error) {
      toast({
        title: t("error"),
        description: t('failedToSignOut'),
        variant: "destructive",
      });
    }
  };

  // Nur die Zahl fuer das Badge holen. Frueher lud die Seitenleiste dafuer
  // ueber useConversations die komplette Konversationsliste -- auf jeder Seite,
  // weil die Seitenleiste im Layout steckt.
  const { unreadCount: unreadConversationsCount } = useUnreadConversationsCount(
    currentTenant?.id || null
  );

  // Get pending tasks count (with real-time subscription)
  const { pendingTasksCount } = useTasks(currentTenant?.id || null);

  // Get tenant config for module flags
  const { hasCalendar } = useTenantConfig();

  // Staff (own-calendar-only logins) see the calendar — plus the customer list
  // only when the salon granted full customer access.
  const isStaff = useIsStaff();
  const staffOpts = { customerList: staffHasFullCustomerAccess(currentTenant?.settings) };

  const topMenuItems = filterNavItemsForStaff([
    { path: "/dashboard", label: t("dashboard"), icon: BarChart3 },
  ], isStaff, staffOpts);

  const middleMenuItems = filterNavItemsForStaff([
    ...(hasCalendar ? [{ path: "/calendar", label: t("calendar"), icon: Calendar }] : []),
    { path: "/conversations", label: t("conversations"), icon: MessageSquare, badge: unreadConversationsCount },
    { path: "/tasks", label: t("tasks"), icon: CheckCircle2, badge: pendingTasksCount },
    { path: "/customers", label: t("customers"), icon: User },
  ], isStaff, staffOpts);

  const { isPlatformAdmin } = useIsPlatformAdmin();

  const bottomMenuItems = filterNavItemsForStaff([
    { path: "/services", label: t("services"), icon: Clock },
    { path: "/availability", label: t("availability"), icon: CalendarDays },
    { path: "/analytics", label: t("analytics"), icon: TrendingUp },
    { path: "/referrals", label: t("referral.nav"), icon: Gift },
    { path: "/settings", label: t("settings"), icon: Settings },
    // Sylva staff only. Hiding the entry is convenience, not protection: the
    // console's RPCs refuse anyone else regardless of how they got there.
    ...(isPlatformAdmin
      ? [{ path: "/admin", label: t("adminConsoleTitle"), icon: ShieldCheck }]
      : []),
  ], isStaff, staffOpts);

  const renderMenuItem = (item: { path: string; label: string; icon: any; badge?: number }) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path ||
      location.pathname.startsWith(item.path + "/") ||
      (item.path === "/dashboard" && location.pathname === "/");

    const menuItemContent = (
      <Link
        to={createPath(item.path)}
        onClick={onNavigate}
        // Frueher wurde hier auch der komplette Kundenstamm vorgeladen. Das
        // waren bei M&M Hairdesign acht aufeinanderfolgende Anfragen (der
        // Kundenstamm wird in 1000er-Bloecken geholt), ausgeloest vom blossen
        // Ueberfahren mit der Maus -- und zusaetzlich zu der Ladung, die der
        // TenantContext ohnehin schon startet. Die Dienstleistungen sind eine
        // einzelne kleine Abfrage und bleiben.
        onMouseEnter={async () => {
          if (item.path === '/services' && currentTenant?.id) {
            await queryClient.prefetchQuery({
              queryKey: ['services', currentTenant.id],
              queryFn: () => fetchServices(currentTenant.id!),
              staleTime: 30 * 60 * 1000,
              gcTime: 2 * 60 * 60 * 1000,
            });
          }
        }}
        onFocus={async () => {
          if (item.path === '/services' && currentTenant?.id) {
            await queryClient.prefetchQuery({
              queryKey: ['services', currentTenant.id],
              queryFn: () => fetchServices(currentTenant.id!),
              staleTime: 30 * 60 * 1000,
              gcTime: 2 * 60 * 60 * 1000,
            });
          }
        }}
        className={cn(
          "flex items-center rounded-lg transition-colors h-12 px-3 py-3 relative",
          isActive
            ? "bg-white/15 text-primary-foreground font-medium"
            : "text-primary-foreground/75 hover:bg-white/10 hover:text-primary-foreground"
        )}
      >
        <Icon className={cn("h-5 w-5 flex-shrink-0", !collapsed && "mr-3")} />
        {!collapsed && <span className="whitespace-nowrap">{item.label}</span>}
      </Link>
    );

    // Wrap in tooltip when collapsed
    const wrappedContent = collapsed ? (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="relative">
            {menuItemContent}
            {item.badge !== undefined && item.badge > 0 && (
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full border border-white"></span>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="right">
          <div className="flex items-center gap-2">
            <p>{item.label}</p>
            {item.badge !== undefined && item.badge > 0 && (
              <span className="min-w-[20px] h-5 px-1.5 flex items-center justify-center bg-red-500 text-white text-xs font-semibold rounded-full">
                {item.badge}
              </span>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    ) : (
      <div className="relative">
        {menuItemContent}
        {item.badge !== undefined && item.badge > 0 && (
          <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full border border-white"></span>
        )}
      </div>
    );

    return wrappedContent;
  };
  return (
    <TooltipProvider>
      <div
        onMouseEnter={handleSidebarMouseEnter}
        onMouseLeave={handleSidebarMouseLeave}
        className={cn(
        "bg-primary border-r border-white/10 h-screen flex flex-col fixed left-0 top-0 z-40 transition-all duration-300",
        collapsed ? "w-20" : "w-54",
        sidebarMode === "hover" && !collapsed && "shadow-2xl"
      )}>
        <div className="border-b border-white/10">
          <div className={cn("px-4 sm:px-6 h-[60px]", collapsed && "px-3")}>
            <div className="flex items-center gap-3 h-full">
              <img
                src={SylvaLogo}
                alt="Sylva Platform Logo"
                className="h-8 w-8 min-h-[36px] min-w-[36px] flex-shrink-0 ml-1 brightness-0 invert"
              />
            </div>
          </div>
        </div>

        <nav className="flex-1 p-3">
          <div className="space-y-2">
            {/* Top Section - Dashboard */}
            <ul className="space-y-1">
              {topMenuItems.map((item) => (
                <li key={item.path}>
                  {renderMenuItem(item)}
                </li>
              ))}
            </ul>

            {/* Separator */}
            <div className="border-t border-white/10"></div>

            {/* Middle Section - Customer & Calendar */}
            <ul className="space-y-1">
              {middleMenuItems.map((item) => (
                <li key={item.path}>
                  {renderMenuItem(item)}
                </li>
              ))}
            </ul>

            {/* Separator */}
            <div className="border-t border-white/10"></div>

            {/* Bottom Section - Appointments & Others */}
            <ul className="space-y-1">
              {bottomMenuItems.map((item) => (
                <li key={item.path}>
                  {renderMenuItem(item)}
                </li>
              ))}
            </ul>
          </div>
        </nav>

        {/* Account section at bottom */}
        <div className="border-t border-white/10 p-3 space-y-1">
          {/* Tenant switcher - only for multi-tenant users */}
          {tenants.length > 1 && (
            collapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Select
                    open={tenantMenuOpen}
                    onOpenChange={setTenantMenuOpen}
                    value={currentTenant?.id || ""}
                    onValueChange={(tenantId) => {
                      const tenant = tenants.find(t => t.id === tenantId);
                      if (tenant) setCurrentTenant(tenant);
                    }}
                  >
                    <SelectTrigger className="h-12 w-full border-none shadow-none bg-transparent text-primary-foreground px-3 py-3 hover:bg-white/10 rounded-lg justify-center [&>svg]:hidden focus:ring-0 focus:ring-offset-0">
                      <div className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center text-primary-foreground font-semibold text-sm">
                        {currentTenant?.name?.charAt(0).toUpperCase() || "?"}
                      </div>
                    </SelectTrigger>
                    <SelectContent side="right">
                      {tenants.map((tenant) => (
                        <SelectItem key={tenant.id} value={tenant.id}>
                          {tenant.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>{currentTenant?.name || t("selectBusiness")}</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <Select
                open={tenantMenuOpen}
                onOpenChange={setTenantMenuOpen}
                value={currentTenant?.id || ""}
                onValueChange={(tenantId) => {
                  const tenant = tenants.find(t => t.id === tenantId);
                  if (tenant) setCurrentTenant(tenant);
                }}
              >
                <SelectTrigger className="h-12 w-full border-none shadow-none bg-transparent text-primary-foreground px-3 py-3 hover:bg-white/10 rounded-lg focus:ring-0 focus:ring-offset-0 overflow-hidden">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center text-primary-foreground font-semibold text-sm flex-shrink-0">
                      {currentTenant?.name?.charAt(0).toUpperCase() || "?"}
                    </div>
                    <span className="truncate text-sm font-medium text-primary-foreground">{currentTenant?.name || t("selectBusiness")}</span>
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {tenants.map((tenant) => (
                    <SelectItem key={tenant.id} value={tenant.id}>
                      {tenant.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )
          )}

          {/* Sidebar display control (expanded / collapsed / expand on hover) */}
          {onSidebarModeChange && (
            <DropdownMenu open={controlMenuOpen} onOpenChange={setControlMenuOpen}>
              <DropdownMenuTrigger asChild>
                <button
                  className={cn(
                    "flex items-center rounded-lg transition-colors h-12 px-3 py-3 w-full",
                    "text-primary-foreground/75 hover:bg-white/10 hover:text-primary-foreground",
                    collapsed && "justify-center"
                  )}
                >
                  <PanelLeft className={cn("h-5 w-5 flex-shrink-0", !collapsed && "mr-3")} />
                  {!collapsed && <span className="whitespace-nowrap">{t("sidebarControl")}</span>}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="top" align="start" className="w-56">
                <DropdownMenuLabel>{t("sidebarControl")}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuRadioGroup
                  value={sidebarMode}
                  onValueChange={(v) => onSidebarModeChange(v as SidebarMode)}
                >
                  <DropdownMenuRadioItem value="expanded">{t("sidebarExpanded")}</DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="collapsed">{t("sidebarCollapsed")}</DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="hover">{t("sidebarExpandOnHover")}</DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Logout button */}
          {(() => {
            const logoutButton = (
              <button
                onClick={handleSignOut}
                className={cn(
                  "flex items-center rounded-lg transition-colors h-12 px-3 py-3 w-full",
                  "text-primary-foreground/75 hover:bg-white/10 hover:text-primary-foreground",
                  collapsed && "justify-center"
                )}
              >
                <LogOut className={cn("h-5 w-5 flex-shrink-0", !collapsed && "mr-3")} />
                {!collapsed && <span className="whitespace-nowrap">{t("signOut")}</span>}
              </button>
            );

            if (collapsed) {
              return (
                <Tooltip>
                  <TooltipTrigger asChild>
                    {logoutButton}
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    <p>{t("signOut")}</p>
                  </TooltipContent>
                </Tooltip>
              );
            }

            return logoutButton;
          })()}
        </div>
      </div>
    </TooltipProvider>
  );
};
